import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useRef, useState } from "react";

import {
  type ManagementType,
  type WorkerApplicationPreferenceDetails,
} from "../../../api/useCreatePlacementApplication";
import { type InterviewTime } from "../../InterviewTimeSelector";
import { type PlacementActionType } from "../utils/getPrimaryAction";

export type ChildcareChatContinuationAction =
  | "apply"
  | "easyScheduling"
  | "bookInterview"
  | undefined;

/**
 * Manages all modal and component state for placement actions.
 * This is a pure state container with no business logic.
 */
export function usePlacementState() {
  // Modal states
  const placementApplicationSuccessModalState = useModalState();
  const bookInterviewModalState = useModalState();
  const walkInNoteModalState = useModalState();
  const showAlternativeAddressModalState = useModalState();
  const createApplicationModalState = useModalState();
  const locationPermissionModalState = useModalState();
  const applicationPreferenceModalState = useModalState();
  const externalApplicationModalState = useModalState();
  const externalApplicationConfirmationModalState = useModalState();
  const interviewBookedModalState = useModalState();
  const interviewReliabilityModalState = useModalState();
  const cancelInterviewModalState = useModalState();
  const lateRescheduleModalState = useModalState();
  const interviewLocationPermissionModalState = useModalState();
  const cancelPastInterviewModalState = useModalState();
  const lateRescheduleTodayModalState = useModalState();
  const easySchedulingInterviewRequestModalState = useModalState();
  const phoneConfirmationModalState = useModalState();
  const childcareSupportChatModalState = useModalState();
  const screenLastStepScreeningModalState = useModalState();
  const submitInterviewDocumentsLaterModalState = useModalState();
  const submitDocumentsLaterInterviewBookedModalState = useModalState();
  const updateInterviewEtaModalState = useModalState();
  const verifyInterviewAttendanceModalState = useModalState();
  const jobSearchAgreementModalState = useModalState();
  const jobSearchAgreementConfirmationModalState = useModalState();
  const rescheduleOrCancelInterviewModalState = useModalState();
  const interviewDocumentReminderModalState = useModalState();

  // Interview state
  const [selectedInterviewTime, setSelectedInterviewTime] = useState<InterviewTime | undefined>();
  const [bookInterviewModalType, setBookInterviewModalType] =
    useState<PlacementActionType>(undefined);
  const [isRescheduleForToday, setIsRescheduleForToday] = useState(false);
  const [isBookingFromSubmitDocumentsLater, setIsBookingFromSubmitDocumentsLater] = useState(false);

  // Application state
  const [applicationPreferences, setApplicationPreferences] = useState<
    WorkerApplicationPreferenceDetails | undefined
  >();
  const [applicationManagementType, setApplicationManagementType] = useState<
    ManagementType | undefined
  >();
  const pendingActionRef = useRef<
    ((preferences?: WorkerApplicationPreferenceDetails) => Promise<void> | void) | undefined
  >();

  // Location state
  const [isOsWithoutAlwaysAllow, setIsOsWithoutAlwaysAllow] = useState(false);
  const [shouldShowLocationPermissionDialog, setShouldShowLocationPermissionDialog] =
    useState(false);

  const [childcareChatContinuationAction, setChildcareChatContinuationAction] =
    useState<ChildcareChatContinuationAction>(undefined);

  return {
    modals: {
      placementApplicationSuccess: placementApplicationSuccessModalState,
      bookInterview: bookInterviewModalState,
      walkInNote: walkInNoteModalState,
      showAlternativeAddress: showAlternativeAddressModalState,
      createApplication: createApplicationModalState,
      locationPermission: locationPermissionModalState,
      applicationPreference: applicationPreferenceModalState,
      externalApplication: externalApplicationModalState,
      externalApplicationConfirmation: externalApplicationConfirmationModalState,
      interviewBooked: interviewBookedModalState,
      interviewReliability: interviewReliabilityModalState,
      cancelInterview: cancelInterviewModalState,
      lateReschedule: lateRescheduleModalState,
      interviewLocationPermission: interviewLocationPermissionModalState,
      cancelPastInterview: cancelPastInterviewModalState,
      lateRescheduleToday: lateRescheduleTodayModalState,
      easySchedulingInterviewRequest: easySchedulingInterviewRequestModalState,
      phoneConfirmation: phoneConfirmationModalState,
      childcareSupportChat: childcareSupportChatModalState,
      screenLastStepScreening: screenLastStepScreeningModalState,
      submitInterviewDocumentsLater: submitInterviewDocumentsLaterModalState,
      submitDocumentsLaterInterviewBooked: submitDocumentsLaterInterviewBookedModalState,
      updateInterviewEta: updateInterviewEtaModalState,
      verifyInterviewAttendance: verifyInterviewAttendanceModalState,
      jobSearchAgreement: jobSearchAgreementModalState,
      jobSearchAgreementConfirmation: jobSearchAgreementConfirmationModalState,
      rescheduleOrCancelInterview: rescheduleOrCancelInterviewModalState,
      interviewDocumentReminder: interviewDocumentReminderModalState,
    },
    interviewState: {
      selectedInterviewTime,
      setSelectedInterviewTime,
      bookInterviewModalType,
      setBookInterviewModalType,
      isRescheduleForToday,
      setIsRescheduleForToday,
      isBookingFromSubmitDocumentsLater,
      setIsBookingFromSubmitDocumentsLater,
    },
    applicationState: {
      applicationPreferences,
      setApplicationPreferences,
      applicationManagementType,
      setApplicationManagementType,
      pendingActionRef,
    },
    locationState: {
      isOsWithoutAlwaysAllow,
      setIsOsWithoutAlwaysAllow,
      shouldShowLocationPermissionDialog,
      setShouldShowLocationPermissionDialog,
    },
    childcareChatState: {
      childcareChatContinuationAction,
      setChildcareChatContinuationAction,
    },
  };
}
